// Creating a video, and duplicating one.
//
// The fields come from the schema rather than a hand-maintained literal -- the
// old dialog repeated its nine-key field object five times, so adding a field
// meant editing the same literal in five places or having them drift.
//
// On success the caller drops straight into the Media tab: a new record with
// no file is not something you want to go hunting for afterwards.

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useAdmin } from './AdminStore';
import { describeError, patchVideo } from './api';
import { FIELDS, emptyFields } from './schema';
import { parseProducts, serializeProducts, writeBool } from '../ps/format';

// Identifiers become part of a public URL and are the KV key, so they are
// constrained here rather than discovered to be broken later.
const ID_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

export function NewVideoDialog({ open, onClose, onCreated, duplicateOf }) {
  const { videos, knownCategories, knownProducts, applyRecord, toast } = useAdmin();

  const [identifier, setIdentifier] = useState('');
  const [fields, setFields] = useState(emptyFields);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Read through a ref, not the effect's deps: the catalog is a new object on
  // every refresh and every save, and depending on it meant a refresh landing
  // while the dialog was open wiped the identifier and every field typed so
  // far. The reset belongs to opening the dialog, not to the catalog changing.
  const videosRef = useRef(videos);
  videosRef.current = videos;

  useEffect(() => {
    if (!open) return;
    const catalog = videosRef.current;
    setError('');
    setIdentifier('');
    if (duplicateOf && catalog[duplicateOf]) {
      // Everything except the title and the files: a duplicate exists because
      // the settings are a near-copy, not because the content is.
      const source = catalog[duplicateOf];
      setFields({
        ...emptyFields(),
        category: source.category || '',
        products: parseProducts(source.products),
        linktext: source.linktext || '',
        link: source.link || '',
        chatbot: String(source.chatbot).toLowerCase() !== 'false',
        // Never inherit "live": a video with no file must not go straight onto
        // the home page.
        frontend: false,
        public: false,
      });
    } else {
      setFields(emptyFields());
    }
  }, [open, duplicateOf]);

  const id = identifier.trim().toLowerCase();
  // hasOwnProperty, not truthiness: `videos.constructor` is inherited from
  // Object.prototype and would report the identifier "constructor" as taken.
  const idTaken = Object.prototype.hasOwnProperty.call(videos, id);
  const idValid = ID_PATTERN.test(id);
  const canCreate = id && idValid && !idTaken && !busy;

  const set = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const patch = { assets: { hls: '', mp4: '' }, subtitles: [], chapters: [] };
      FIELDS.forEach((field) => {
        const v = fields[field.key];
        if (field.type === 'flag' || field.type === 'optout') patch[field.key] = writeBool(v);
        else if (field.type === 'products') patch[field.key] = serializeProducts(v);
        else patch[field.key] = v;
      });

      applyRecord(id, await patchVideo(id, patch));
      toast(`Created ${id}.`, 'success');
      onCreated(id);
    } catch (err) {
      setError(describeError(err, 'The video was not created.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{duplicateOf ? `New video from ${duplicateOf}` : 'New video'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: '18px' }}>
          <span className="admin-label">Identifier</span>
          <Typography sx={{ fontSize: 10.5, color: 'var(--ps-text-dim)', mb: '4px' }}>
            Lowercase, no spaces. This is the URL and it cannot be changed later.
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            error={!!id && (!idValid || idTaken)}
            helperText={
              !id
                ? ' '
                : idTaken
                  ? 'A video with that identifier already exists.'
                  : !idValid
                    ? 'Use lowercase letters, digits and hyphens only.'
                    : `learn.philspeiser.com/watch/${id}`
            }
            inputProps={{ className: 'admin-mono' }}
          />
        </Box>

        {FIELDS.filter((f) => ['title', 'category', 'products'].includes(f.key)).map((field) => (
          <Box key={field.key} sx={{ mb: '18px' }}>
            <span className="admin-label">{field.label}</span>
            <Box sx={{ mt: '4px' }}>
              {field.type === 'text' && (
                <TextField
                  fullWidth
                  size="small"
                  value={fields[field.key]}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}
              {field.type === 'category' && (
                <Autocomplete
                  freeSolo
                  size="small"
                  options={knownCategories}
                  value={fields[field.key]}
                  onInputChange={(_, v) => set(field.key, v || '')}
                  renderInput={(params) => <TextField {...params} />}
                />
              )}
              {field.type === 'products' && (
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={knownProducts}
                  value={fields[field.key]}
                  // Through parseProducts, not raw: freeSolo will hand back
                  // the same name twice if it is typed twice.
                  onChange={(_, v) => set(field.key, parseProducts(v))}
                  renderTags={(list, getTagProps) =>
                    list.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip key={key} label={option} size="small" {...tagProps} />;
                    })
                  }
                  renderInput={(params) => <TextField {...params} placeholder="Add a product" />}
                />
              )}
            </Box>
          </Box>
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Switch
            checked={fields.public}
            onChange={(e) => set('public', e.target.checked)}
            slotProps={{ input: { 'aria-label': 'Public' } }}
          />
          <span className="admin-label">Public — free for everyone</span>
        </Box>

        <Alert severity="info" sx={{ mt: '18px' }}>
          The video starts hidden. Upload a file, add artwork and subtitles, then turn on
          “Live on home” when it is ready.
        </Alert>

        {error && <Alert severity="error" sx={{ mt: '10px' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={!canCreate}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NewVideoDialog;
